'use client';

import { Chapter, Page } from '@/hooks/useChapter';
import { Subject } from '@/hooks/useSubject';
import { slugify } from '@/utils/strings';
import Link from 'next/link';
import { useLayoutEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { findLast } from '@/utils/arrays';

export interface PageNavigationItemProps {
    subject: Subject;
    chapter: Chapter;
    page: Page;
    index: number;
    isActive: boolean;
}

const PageNavigationItem = ({
    subject,
    chapter,
    page,
    index,
    isActive,
}: PageNavigationItemProps) => {
    const [showElement, setShowElement] = useState<boolean>(true);

    // Only show after initial render to get rid of 'html doesn't match server' error
    useLayoutEffect(() => {
        requestAnimationFrame(() => {
            setShowElement(true);
        });
    }, []);

    const { subjectProgress } = useSubjectProgress(subject.id);

    const isVisited = subjectProgress.pages[page.id] && showElement;

    const pageProgressFactor = useMemo(() => {
        if (isVisited) return 1;

        const lastBlockWithProgress = findLast<number>(
            page.block_ids,
            (block_id) => !!subjectProgress.blocks[block_id],
        );

        if (!lastBlockWithProgress) return 0;

        const lastBlockWithProgressIndex = page.block_ids.indexOf(
            lastBlockWithProgress,
        );

        return lastBlockWithProgressIndex / page.block_ids.length;
    }, [page.block_ids, subjectProgress.blocks, isVisited]);

    const fullColor = isVisited
        ? 'bg-green-600 hover:outline-green-600 focus:outline-green-600'
        : 'bg-primary-text-lg-aa hover:outline-primary-text-lg-aa focus:outline-primary-text-lg-aa';

    let content = (
        <>
            <span className="sr-only">Pagina {index + 1}</span>
            {isActive && <span className="sr-only"> (huidige pagina)</span>}
        </>
    );

    if (isActive || isVisited) {
        content = (
            <Link
                href={`/story/${subject.id}/${slugify(subject.title)}/${
                    chapter?.id
                }/${slugify(chapter?.title ?? '')}/${page.id}`}
                passHref={true}
                className="absolute inset-0 h-full w-full"
                title={`Pagina ${index + 1} ${
                    isActive ? '(huidige pagina)' : ''
                }`}
                aria-current={isActive ? 'page' : undefined}
            >
                {content}
            </Link>
        );
    }

    return (
        <motion.li
            initial={{
                opacity: isActive ? 1 : 0.2,
            }}
            animate={{
                opacity: 1,
            }}
            className={`${isActive || isVisited ? fullColor : 'bg-slate-200'} ${
                isActive && pageProgressFactor < 1
                    ? 'bg-opacity-40 hover:bg-opacity-25'
                    : ''
            } relative block h-full min-w-[3rem] rounded-full outline outline-2 outline-transparent active:outline-0`}
            style={{
                flexGrow: isActive ? 1 : 0.5,
            }}
            layoutId={`page-navigation-item-${page.id}`}
        >
            {content}
            {/* {isActive && (
                <div
                    className={`absolute -bottom-1 left-1/2 -translate-x-1/2 rotate-45 h-2 w-2 rounded-sm ${fullColor}`}
                />
            )} */}
            {(isActive || isVisited) && pageProgressFactor < 1 && (
                <div className="absolute inset-0 h-full w-full overflow-hidden rounded-full">
                    <motion.div
                        className={`absolute inset-0 h-full rounded-full ${fullColor} cursor-pointer`}
                        initial={{
                            minWidth: 0,
                            width: 0,
                        }}
                        animate={{
                            minWidth: `max(2.5rem, ${
                                1 / page.block_ids.length
                            }%)`,
                            width: `${pageProgressFactor * 100}%`,
                            boxShadow: '0 0 0 0.2rem white',
                            transition: {
                                duration: 6,
                            },
                        }}
                    />
                </div>
            )}
        </motion.li>
    );
};

export default PageNavigationItem;
